// Theme tokens for styled-components, mapped to AntD tokens where appropriate
// Supports both light and dark modes with swapped secondary/tertiary colors

const baseTheme = {
    colors: {
        primary: '#7543DC',
        success: '#52c41a',
        warning: '#faad14',
        error: '#ff4d4f',
        info: '#1890ff',
        link: '#1890ff',
        linkHover: '#0077be',
    },
    fonts: {
        geist: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        family: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        weights: {
            thin: 100,
            extraLight: 200,
            light: 300,
            regular: 400,
            medium: 500,
            semiBold: 600,
            bold: 700,
            extraBold: 800,
            black: 900,
        },
    },
    spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        xxl: '48px',
    },
    borderRadius: {
        sm: '4px',
        md: '8px',
        lg: '12px',
        xl: '16px',
    },
    breakpoints: {
        xs: '480px',
        sm: '576px',
        md: '768px',
        lg: '992px',
        xl: '1200px',
        xxl: '1600px',
    },
    zIndex: {
        dropdown: 1050,
        sticky: 1020,
        fixed: 1030,
        modal: 1040,
        popover: 1060,
        tooltip: 1070,
    },
    /** Modal width presets: sm (alerts/confirmations), md (small forms), lg (full forms – 85vw, 90vh). */
    modalSizes: {
        sm: 400,
        md: 520,
        lg: '85vw',
    },
};

// Light theme - secondary: #fafafa, tertiary: #f5f5f5
const lightTheme = {
    ...baseTheme,
    colors: {
        ...baseTheme.colors,
        text: {
            primary: '#F04A4A',
            secondary: 'rgba(0, 0, 0, 0.65)',
            disabled: 'rgba(0, 0, 0, 0.25)',
        },
        background: {
            default: '#ffffff',
            primary: '#7543DC',
            secondary: '#fafafa', // Light mode secondary
            tertiary: '#f5f5f5', // Light mode tertiary
        },
        border: {
            default: '#d9d9d9',
            primary: '#7543DC',
            light: '#f0f0f0',
        },
    },
};

// Dark theme - secondary and tertiary are swapped
const darkTheme = {
    ...baseTheme,
    colors: {
        ...baseTheme.colors,
        text: {
            primary: '#F04A4A', // Primary stays same
            secondary: 'rgba(255, 255, 255, 0.65)',
            disabled: 'rgba(255, 255, 255, 0.25)',
        },
        background: {
            default: '#141414',
            primary: '#7543DC',
            secondary: '#1f1f1f', // Dark mode secondary (swapped from light tertiary)
            tertiary: '#262626', // Dark mode tertiary (swapped from light secondary)
        },
        border: {
            default: '#434343',
            primary: '#7543DC',
            light: '#303030',
        },
    },
};

/**
 * Get theme based on mode
 */
export const getTheme = (mode = 'light') => {
    return mode === 'dark' ? darkTheme : lightTheme;
};

// Default export (light theme for backward compatibility)
export const theme = lightTheme;

/**
 * Get Ant Design theme configuration based on mode
 */
export const getAntdTheme = (mode = 'light') => {
    const currentTheme = getTheme(mode);
    /** Dark: avoid pure black fields; muted placeholder (not white) */
    const darkFieldBg = '#252525';
    const darkFieldHoverBg = '#2e2e2e';
    const darkFieldBorder = 'rgba(255, 255, 255, 0.14)';
    const darkPlaceholder = 'rgba(255, 255, 255, 0.45)';

    return {
        defaultSize: 'large',
        token: {
            fontFamily: currentTheme.fonts.family,
            fontWeightStrong: 600,
            fontSize: 16,
            colorPrimary: currentTheme.colors.primary,
            colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#1f1f1f',
            colorTextHeading: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#1a1a1a',
            colorBgContainer: mode === 'dark' ? '#141414' : '#ffffff',
            colorBgElevated: mode === 'dark' ? currentTheme.colors.background.secondary : '#ffffff',
            colorBgLayout: mode === 'dark' ? '#000000' : currentTheme.colors.background.tertiary,
            colorBorder: mode === 'dark' ? '#434343' : '#d9d9d9',
            borderRadius: 6,
            borderRadiusSM: 6,
            borderRadiusLG: 6,
            borderRadiusXS: 6,
        },
        components: {
            Typography: {
                fontFamily: currentTheme.fonts.geist,
                titleFontFamily: currentTheme.fonts.geist,
                headingFontFamily: currentTheme.fonts.geist,
                fontWeightStrong: 700,
            },
            Input: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                activeBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                hoverBg: mode === 'light' ? '#ffffff' : darkFieldHoverBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            InputPassword: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                activeBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                hoverBg: mode === 'light' ? '#ffffff' : darkFieldHoverBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            Select: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                selectorBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            Button: {
                size: 'large',
                borderRadius: 6,
                colorPrimary: currentTheme.colors.primary,
                colorPrimaryHover: '#8b63e8',
                colorPrimaryActive: '#5f32c4',
                defaultBg: mode === 'dark' ? '#434343' : '#747474',
                defaultBorderColor: mode === 'dark' ? '#434343' : '#747474',
                defaultColor: '#ffffff',
                defaultHoverBg: mode === 'dark' ? '#595959' : '#8b8b8b',
                defaultHoverBorderColor: mode === 'dark' ? '#595959' : '#8b8b8b',
                defaultActiveBg: mode === 'dark' ? '#303030' : '#616161',
                defaultActiveBorderColor: mode === 'dark' ? '#303030' : '#616161',
            },
            DatePicker: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                activeBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                hoverBg: mode === 'light' ? '#f5f5f5' : darkFieldHoverBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            RangePicker: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                activeBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                hoverBg: mode === 'light' ? '#f5f5f5' : darkFieldHoverBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            TimePicker: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                activeBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                hoverBg: mode === 'light' ? '#f5f5f5' : darkFieldHoverBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            Cascader: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                selectorBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            AutoComplete: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                activeBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                hoverBg: mode === 'light' ? '#f5f5f5' : darkFieldHoverBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            TreeSelect: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                selectorBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            InputNumber: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                activeBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                hoverBg: mode === 'light' ? '#f5f5f5' : darkFieldHoverBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            Mentions: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                activeBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                hoverBg: mode === 'light' ? '#f5f5f5' : darkFieldHoverBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            InputOTP: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : darkFieldBg,
                activeBg: mode === 'light' ? '#ffffff' : darkFieldBg,
                hoverBg: mode === 'light' ? '#f5f5f5' : darkFieldHoverBg,
                colorBorder: mode === 'dark' ? darkFieldBorder : '#d9d9d9',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? darkPlaceholder : 'rgba(0, 0, 0, 0.25)',
            },
            Form: {
                size: 'large',
            },
            Pagination: {
                size: 'large',
            },
            Tabs: {
                itemActiveColor: '#ffffff',
                itemSelectedColor: '#ffffff',
                itemSelectedBg: mode === 'dark' ? '#434343' : '#292929',
                itemHoverColor: mode === 'dark' ? '#595959' : '#292929',
                inkBarColor: currentTheme.colors.primary,
                cardBg: mode === 'dark' ? currentTheme.colors.background.secondary : '#292929',
            },
            Menu: {
                itemSelectedBg: mode === 'dark' ? currentTheme.colors.background.secondary : '#fafafa',
                itemHoverBg: mode === 'dark' ? currentTheme.colors.background.tertiary : '#f5f5f5',
                itemActiveBg: mode === 'dark' ? '#434343' : '#e6f7ff',
                itemSelectedColor: currentTheme.colors.primary,
                itemHoverColor: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                itemColor: mode === 'dark' ? '#8A98A7' : 'rgba(0, 0, 0, 0.65)',
                subMenuItemBg: mode === 'dark' ? currentTheme.colors.background.secondary : '#ffffff',
            },
            Layout: {
                bodyBg: mode === 'dark' ? '#000000' : currentTheme.colors.background.tertiary,
                headerBg: mode === 'dark' ? '#141414' : '#ffffff',
                siderBg: mode === 'dark' ? '#141414' : '#ffffff',
            },
            Card: {
                borderRadius: 6,
                colorTextHeading: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#1a1a1a',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#1f1f1f',
            },
            Breadcrumb: {
                linkColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                separatorColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                itemColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                lastItemColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
            },
            Table: {
                borderRadius: 6,
            },
            Space: {
                size: 'large',
            },
        },
    };
};

export default theme;
